import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";
import { SectionLoading, SectionError } from "@/components/RichContent";
import NotFound from "@/pages/NotFound";
import { useCaseStudy } from "@/hooks/usePublicData";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { data, isLoading, isError, error, refetch } = useCaseStudy(slug);

  if (isLoading) return <SectionLoading label="Loading case study" />;
  if (isError && error && error.status === 404) return <NotFound />;
  if (isError) return <SectionError onRetry={refetch} />;

  const cs = data.item;
  const related = data.related || [];

  // Only render sections the editor actually filled in — an empty heading with
  // nothing under it reads as a broken page.
  const sections = [
    { k: "Client profile", v: cs.clientProfile },
    { k: "Situation", v: cs.clientSituation },
    { k: "Challenge", v: cs.challenges },
    { k: "Research", v: cs.researchConducted },
    { k: "Advisory approach", v: cs.advisoryApproach },
    { k: "Recommended strategy", v: cs.recommendedStrategy },
    { k: "Outcome", v: cs.outcome },
    { k: "Lessons", v: cs.keyLessons },
  ].filter((s) => s.v && String(s.v).trim());

  return (
    <>
      <SeoHead title={cs.seo.title} description={cs.seo.description} />

      <section className="relative bg-[#060f1e] pt-8 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
            data-testid="case-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> All case studies
          </Link>

          <div className="mt-10 grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-8">
              <span className="overline">
                {[cs.category, cs.kicker].filter(Boolean).join(" · ")}
              </span>
              <FadeUp delay={0.1}>
                <h1 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl text-[#f7f5f0] font-light tracking-tight leading-[0.98]">
                  {cs.title}
                </h1>
              </FadeUp>
              {cs.summary && (
                <FadeUp delay={0.2}>
                  <p className="mt-8 text-[#d8d8d8] text-lg leading-relaxed max-w-3xl">{cs.summary}</p>
                </FadeUp>
              )}
            </div>
          </div>
        </div>
      </section>

      {cs.featuredImage && (
        <section className="bg-[#060f1e]">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="relative aspect-[16/9] overflow-hidden">
              <img
                src={cs.featuredImage}
                alt={cs.featuredImageAlt || cs.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#060f1e] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              {cs.objectives && cs.objectives.length > 0 && (
                <>
                  <div className="overline mb-4">Objectives</div>
                  <ul className="space-y-3">
                    {cs.objectives.map((o) => (
                      <li key={o} className="flex gap-3 text-[#d8d8d8] text-sm">
                        <span className="mt-2 h-px w-3 bg-[#cca24d] shrink-0" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {cs.optionsReviewed && cs.optionsReviewed.length > 0 && (
                <>
                  <div className="mt-10 overline mb-4">Options reviewed</div>
                  <ul className="space-y-3">
                    {cs.optionsReviewed.map((o) => (
                      <li key={o} className="flex gap-3 text-[#9ca3af] text-sm">
                        <span className="mt-2 h-px w-3 bg-[#1a2940] shrink-0" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8 space-y-14">
            {sections.map((s, i) => (
              <FadeUp key={s.k} delay={Math.min(i, 6) * 0.05}>
                <div>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="text-xs text-[#cca24d]/60 tnum">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="overline">{s.k}</span>
                  </div>
                  <p className="font-serif text-2xl md:text-3xl text-[#f7f5f0] font-light leading-snug whitespace-pre-line">
                    {s.v}
                  </p>
                </div>
              </FadeUp>
            ))}

            {cs.disclaimer && (
              <div className="pt-10 border-t border-[#1a2940]">
                <div className="overline mb-3">Disclaimer</div>
                <p className="text-sm text-[#9ca3af] leading-relaxed">{cs.disclaimer}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#0c1728] border-t border-[#1a2940] py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="overline mb-8">Related engagements</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((o) => (
                <Link key={o.id} to={o.path} className="group block">
                  {o.category && (
                    <div className="text-xs text-[#cca24d]/80 uppercase tracking-widest mb-3">
                      {o.category}
                    </div>
                  )}
                  <h3 className="font-serif text-2xl text-[#f7f5f0] leading-tight group-hover:text-[#cca24d] transition-colors">
                    {o.title}
                  </h3>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#d8d8d8] group-hover:text-[#cca24d] transition-colors">
                    Read <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#060f1e] border-t border-[#1a2940] py-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="overline mb-2">Next engagement</div>
            <div className="font-serif text-2xl md:text-3xl text-[#f7f5f0]">
              Continue reading case studies.
            </div>
          </div>
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 px-6 h-12 border border-[#1a2940] text-[#f7f5f0] hover:border-[#cca24d] hover:text-[#cca24d] transition-colors text-sm"
          >
            All case studies <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </>
  );
}
